const STORAGE_KEY = 'mithaq_auth';

export function saveAuth(payload) {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      accessToken: payload.accessToken,
      expiresAtUtc: payload.expiresAtUtc,
      user: payload.user,
    }),
  );
}

export function getAuth() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearAuth() {
  localStorage.removeItem(STORAGE_KEY);
}

export function getAccessToken() {
  return getAuth()?.accessToken ?? null;
}

export function getCurrentUser() {
  return getAuth()?.user ?? null;
}

export function getUserDisplayName() {
  const user = getCurrentUser();
  if (!user) return '';
  return String(user.fullName || user.userName || user.email || '').trim();
}

/** @param {string[]|undefined} roles */
export function defaultPathForRoles(roles) {
  if (!roles?.length) return '/app/dashboard';
  if (roles.includes('SuperAdmin')) return '/super-admin/dashboard';
  if (roles.includes('Secretary')) return '/app/dashboard';
  if (roles.includes('SecretaryAgent')) return '/app/dashboard';
  if (roles.includes('Officer')) return '/officer/dashboard';
  if (roles.includes('BoardMemberAgent')) return '/member/dashboard';
  if (roles.includes('BoardMember')) return '/member/dashboard';
  if (roles.includes('ShareholderAgent')) return '/shareholder/dashboard';
  if (roles.includes('Shareholder')) return '/shareholder/dashboard';
  return '/app/dashboard';
}

export function isSuperAdminContextUser() {
  const roles = getAuth()?.user?.roles ?? [];
  return roles.includes('SuperAdmin');
}

function parsePermissionsFromAuth() {
  const auth = getAuth();
  const raw = auth?.user?.permissionsJson;
  if (!raw || typeof raw !== 'string') return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function isOfficerWithoutCustomPermissions() {
  const auth = getAuth();
  const roles = auth?.user?.roles ?? [];
  if (!roles.includes('Officer')) return false;
  const perms = parsePermissionsFromAuth();
  return Object.keys(perms).length === 0;
}

function parseBoardPermissionsFromAuth() {
  const auth = getAuth();
  const raw = auth?.user?.boardPermissionsJson;
  if (!raw || typeof raw !== 'string') return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/** Sub-accounts (SecretaryAgent/Officer) use permissionsJson; principal Secretary has full secretary app access. */
export function isManagedSecretaryUser() {
  const user = getAuth()?.user;
  if (!user) return false;
  if (user.roles?.includes('Secretary')) return false;
  return !!user.isManagedBySecretary || user.roles?.includes('SecretaryAgent') || user.roles?.includes('Officer');
}

export function isSecretaryContextUser() {
  const roles = getAuth()?.user?.roles ?? [];
  return roles.includes('Secretary') || roles.includes('SecretaryAgent') || roles.includes('Officer');
}

export function isOfficerContextUser() {
  const roles = getAuth()?.user?.roles ?? [];
  return roles.includes('Officer') && !roles.includes('Secretary');
}

/** Sub-accounts use boardPermissionsJson; principal BoardMember has full member portal access. */
export function isManagedBoardMemberUser() {
  const user = getAuth()?.user;
  if (!user) return false;
  if (user.roles?.includes('BoardMember')) return false;
  return !!user.isManagedByBoardMember || user.roles?.includes('BoardMemberAgent');
}

export function isBoardMemberContextUser() {
  const roles = getAuth()?.user?.roles ?? [];
  return roles.includes('BoardMember') || roles.includes('BoardMemberAgent');
}

/** Sub-accounts use shareholderPermissionsJson; principal Shareholder has full shareholder portal access. */
export function isManagedShareholderUser() {
  const user = getAuth()?.user;
  if (!user) return false;
  if (user.roles?.includes('Shareholder')) return false;
  return !!user.isManagedByShareholder || user.roles?.includes('ShareholderAgent');
}

export function isShareholderContextUser() {
  const roles = getAuth()?.user?.roles ?? [];
  return roles.includes('Shareholder') || roles.includes('ShareholderAgent');
}

export function hasPagePermission(moduleId) {
  if (moduleId === 'dashboard') return true; // dashboard is always accessible
  if (isOfficerWithoutCustomPermissions()) return true;
  if (!isManagedSecretaryUser()) return true;
  const node = parsePermissionsFromAuth()?.[moduleId];
  return !!node?.pageAccess;
}

export function hasActionPermission(moduleId, actionId) {
  if (isOfficerWithoutCustomPermissions()) return true;
  if (!isManagedSecretaryUser()) return true;
  const node = parsePermissionsFromAuth()?.[moduleId];
  return !!node?.actions?.[actionId];
}

export function hasUiPermission(moduleId, uiId) {
  if (isOfficerWithoutCustomPermissions()) return true;
  if (!isManagedSecretaryUser()) return true;
  const node = parsePermissionsFromAuth()?.[moduleId];
  return !!node?.ui?.[uiId];
}

export function hasBoardPagePermission(moduleId) {
  if (!isManagedBoardMemberUser()) return true;
  const node = parseBoardPermissionsFromAuth()?.[moduleId];
  return !!node?.pageAccess;
}

function parseShareholderPermissionsFromAuth() {
  const auth = getAuth();
  const raw = auth?.user?.shareholderPermissionsJson;
  if (!raw || typeof raw !== 'string') return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function hasShareholderPagePermission(moduleId) {
  if (!isManagedShareholderUser()) return true;
  const node = parseShareholderPermissionsFromAuth()?.[moduleId];
  return !!node?.pageAccess;
}

export function authHeaders() {
  const raw = getAccessToken();
  const token = typeof raw === 'string' ? raw.trim() : '';
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

export function getTrialInfo() {
  const user = getAuth()?.user;
  if (!user?.isTrialAccount || !user?.trialEndsAtUtc) return null;
  const end = new Date(user.trialEndsAtUtc);
  if (Number.isNaN(end.getTime())) return null;
  return { isTrial: true, trialEndsAtUtc: end.toISOString() };
}
